import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
    VisionProviderDiagnostics,
    VisionProviderOAuthStartResponse,
    VisionProviderSubscription,
    VisionProviderSubscriptionCreatePayload,
    VisionProviderSubscriptionReplacePayload,
    VisionProviderSyncReport,
    VisionSubscriptionConsistencyReport,
} from '../../models/vision-provider-management';
import {PaginatedResponse} from '../vte/vte-api.service';

@Injectable({
    providedIn: 'root',
})
export class VisionProviderSubscriptionApiService {
    private readonly baseUrl = `${environment.baseUrl}vision-provider-subscriptions/`;
    private readonly oauthBaseUrl = `${environment.baseUrl}vision-provider-oauth/`;

    constructor(private http: HttpClient) {
    }

    listSubscriptions(
        providerId?: string,
        includeRemote = false,
        page = 1,
        pageSize = 10,
        includeDeleted = false,
    ): Observable<PaginatedResponse<VisionProviderSubscription>> {
        return this.http.get<PaginatedResponse<VisionProviderSubscription>>(this.baseUrl, {
            params: this.toParams({
                provider_id: providerId,
                include_remote: includeRemote,
                include_deleted: includeDeleted,
                page,
                page_size: pageSize,
            }),
        });
    }

    getSubscription(
        subscriptionId: string,
        providerId?: string,
    ): Observable<VisionProviderSubscription> {
        return this.http.get<VisionProviderSubscription>(`${this.baseUrl}${subscriptionId}/`, {
            params: this.toParams({provider_id: providerId}),
        });
    }

    createSubscription(
        payload: VisionProviderSubscriptionCreatePayload,
    ): Observable<VisionProviderSubscription> {
        return this.http.post<VisionProviderSubscription>(this.baseUrl, payload);
    }

    replaceSubscription(
        subscriptionId: string,
        payload: VisionProviderSubscriptionReplacePayload,
    ): Observable<VisionProviderSubscription> {
        return this.http.post<VisionProviderSubscription>(
            `${this.baseUrl}${subscriptionId}/replace/`,
            payload,
        );
    }

    deleteSubscription(
        subscriptionId: string,
        providerId?: string,
    ): Observable<VisionProviderSubscription> {
        return this.http.delete<VisionProviderSubscription>(`${this.baseUrl}${subscriptionId}/`, {
            params: this.toParams({provider_id: providerId}),
        });
    }

    refreshSubscriptions(providerId?: string): Observable<VisionProviderSyncReport> {
        return this.http.post<VisionProviderSyncReport>(`${this.baseUrl}refresh/`, {
            provider_id: providerId,
        });
    }

    getDiagnostics(providerId?: string): Observable<VisionProviderDiagnostics> {
        return this.http.get<VisionProviderDiagnostics>(`${this.baseUrl}diagnostics/`, {
            params: this.toParams({provider_id: providerId}),
        });
    }

    /** B4 — authoritative KYRA↔EEN consistency (read-only). */
    getConsistencyReport(providerId?: string): Observable<VisionSubscriptionConsistencyReport> {
        return this.http.get<VisionSubscriptionConsistencyReport>(`${this.baseUrl}consistency/`, {
            params: this.toParams({provider_id: providerId}),
        });
    }

    startOAuth(providerId: string): Observable<VisionProviderOAuthStartResponse> {
        return this.http.get<VisionProviderOAuthStartResponse>(`${this.oauthBaseUrl}start/`, {
            params: this.toParams({provider_id: providerId}),
        });
    }

    private toParams(values: Record<string, unknown>): HttpParams {
        let params = new HttpParams();
        Object.entries(values).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        });
        return params;
    }
}
